// Transient, non-persisted cross-panel selection state
// (docs/editor-consolidation/panel-content-spec.md §3.3, second flavour).
//
// A selected-row id, an active tab, a pending-delete confirmation: none of
// this survives a reload and none of it belongs in a mode-document. Once a
// page splits into sibling panels, this pointer's reader(s) and writer(s)
// land in different panels, so it can no longer be a plain useState local to
// one component - but it also must not collide across mode-groups (Logic's
// selectedRuleId and HUD's selectedWidgetId are unrelated) or leak between
// projects.
//
// createTransientSelectionStore builds one such store, scoped by projectId
// and reset the moment every panel using it has unmounted (so switching
// projects, or navigating away and back, never resurrects a stale
// selection) - the same "last consumer out resets it" shape §3.1/§3.2 use
// for persisted state, applied to state that has nothing to flush.
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useEditorHost } from '../EditorHost';

interface Entry<T> {
  value: T;
  refCount: number;
  listeners: Set<() => void>;
}

export interface TransientSelectionStore<T> {
  /** Read/write the current value; shared by every panel using this store
   * for the current project, reset once no panel is using it any more. */
  use(): [T, (next: T | ((current: T) => T)) => void];
}

/** One store per call site (e.g. one per mode-group's selection shape) -
 * call this at module scope, not inside a component. */
export function createTransientSelectionStore<T>(initial: T): TransientSelectionStore<T> {
  const entries = new Map<string, Entry<T>>();

  function notify(entry: Entry<T>): void {
    entry.listeners.forEach((l) => l());
  }

  function acquire(projectId: string): Entry<T> {
    let entry = entries.get(projectId);
    if (!entry) {
      entry = { value: initial, refCount: 0, listeners: new Set() };
      entries.set(projectId, entry);
    }
    entry.refCount += 1;
    return entry;
  }

  function release(projectId: string): void {
    const entry = entries.get(projectId);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount <= 0) entries.delete(projectId);
  }

  function use(): [T, (next: T | ((current: T) => T)) => void] {
    const { projectId } = useEditorHost();
    const [, forceRender] = useReducer((c: number) => c + 1, 0);
    const projectIdRef = useRef(projectId);
    projectIdRef.current = projectId;

    useEffect(() => {
      const entry = acquire(projectId);
      entry.listeners.add(forceRender);
      forceRender();
      return () => {
        entry.listeners.delete(forceRender);
        release(projectId);
      };
    }, [projectId]);

    const setValue = useCallback((next: T | ((current: T) => T)) => {
      const entry = entries.get(projectIdRef.current);
      if (!entry) return;
      entry.value = typeof next === 'function' ? (next as (current: T) => T)(entry.value) : next;
      notify(entry);
    }, []);

    const entry = entries.get(projectId);
    return [entry?.value ?? initial, setValue];
  }

  return { use };
}
